import { useState, useCallback } from "react";
import { sendError } from "@/lib/client/sentry";
import shuffle from "lodash/shuffle";
const API_URL = process.env.NEXT_PUBLIC_API_URL || "";

/**
 * Hook for fetching and updating the user's most recently viewed items.
 *
 * Provides functions to fetch recent views and track new views. Results are
 * shuffled to provide variety on each visit. Cached for 1 hour.
 *
 * @returns Object containing:
 * - fetchMostRecent: Function to fetch recent views with optional limit
 * - updateMostRecent: Function to add an item to recent views
 * - mostRecent: Array of recently viewed items
 * - loading: Boolean indicating if data is being fetched
 *
 * @example
 * ```tsx
 * function RecentViews() {
 *   const { fetchMostRecent, mostRecent, loading } = useMostRecentViews();
 *
 *   useEffect(() => {
 *     fetchMostRecent(50); // Fetch last 50 views
 *   }, []);
 *
 *   return (
 *     <div>
 *       <h3>Recently Viewed</h3>
 *       {loading ? (
 *         <Spinner />
 *       ) : (
 *         mostRecent.map(item => (
 *           <CompactCard key={item.id} {...item} />
 *         ))
 *       )}
 *     </div>
 *   );
 * }
 * ```
 */
const useMostRecentViews = () => {
  const [mostRecent, setMostRecent] = useState<SearchResultItem[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchMostRecent = useCallback(async (limit = 100) => {
    setLoading(true);
    setMostRecent([]);

    try {
      const response = await fetch(`${API_URL}/api/v2/search/get-recent-search?limit=${limit}`, {
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          "Cache-Control": "public, max-age=3600",
        },
      }).then((response) => response.json());

      setMostRecent(shuffle(response?.data));
    } catch (error) {
      console.warn("Error fetching most recent views:", error);
      sendError(error as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  const updateMostRecent = useCallback(async (id: number) => {
    if (!id) return;

    try {
      await fetch(`${API_URL}/api/v2/search/post-recent-search`, {
        credentials: "include",
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ id }),
      });
    } catch (error) {
      console.log("Error updating most recent views:", error);
      sendError(error as Error, { id });
    }
  }, []);

  return {
    fetchMostRecent,
    updateMostRecent,
    mostRecent,
    loading,
  };
};

export default useMostRecentViews;
